/**
 * The console side of core 0030/0031 — personas, view/form resolution, the
 * filter grammar, and small format/display helpers. Everything reads the SAME
 * governed rows the migrations ship (served here by the fixture transport,
 * byte-compatible with live PostgREST).
 */
import { useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useSolum } from '../data/client'
import { currentPrincipalId, isLive } from '@solum/auth'
import { parseSortSpec, type Row, type SolumClient } from '@solum/sdk'
import { parseResolvedExperience } from '@solum/experience'
import { evalLeaf as evalLeafBase, fireRules as fireRulesBase, type Leaf, type RuleAction } from '@solum/rules'
import { formatMoney, formatDate } from '@solum/format'
import { usePersona } from '../app/persona'

/** The fixture's id for the demo user — the offline/demo console keeps working. */
const FIXTURE_ME = 'a1b2c3d4'

/**
 * The acting principal, used in eight places: owner filters, the audit actor,
 * share grants, and `$me` inside filter expressions.
 *
 * It was once the literal `'a1b2c3d4'`, which against a real backend meant
 * `user_id=eq.a1b2c3d4` — not a UUID, so PostgREST answered 400 on every CRM
 * page load. Invisible in fixture mode, which is the only mode the browser suite
 * runs in; found by opening the console against the cutover rehearsal box.
 *
 * Then it was derived from the build-time JWT, which was correct only because
 * that JWT never changed. Now it reads the live session (SEC-03) — hence a
 * FUNCTION rather than a const: the principal is unknown until sign-in and
 * changes on sign-out, so anything captured at import time is the previous user.
 */
export function me(): string {
  return currentPrincipalId() ?? FIXTURE_ME
}

/** Owner display names resolved over the GRAPH: app_user (principal) → person.
 *  Answers by auth principal id OR app_user id — the same double door 0018/0022
 *  leave open (provision-before-login). Replaces the old fixture OWNERS map. */
export function useOwners(): (principal: unknown) => string {
  const { client } = useSolum()
  const users = useQuery({ queryKey: ['exp', 'app_users'], queryFn: () => client.raw().select<Row>('core', 'app_user') })
  const people = useQuery({ queryKey: ['exp', 'people'], queryFn: () => client.raw().select<Row>('core', 'person') })
  const map = useMemo(() => {
    const personById = new Map(((people.data ?? []) as Row[]).map((p) => [String(p.id), p]))
    const out = new Map<string, string>()
    for (const u of (users.data ?? []) as Row[]) {
      const p = personById.get(String(u.person_id))
      if (!p) continue
      const name = String(p.display_name ?? `${p.given_name ?? ''} ${p.family_name ?? ''}`.trim())
      if (u.auth_user_id) out.set(String(u.auth_user_id), name)
      out.set(String(u.id), name)
    }
    return out
  }, [users.data, people.data])
  return (principal) => map.get(String(principal)) ?? '—'
}

/* ── persona: a tiny external store so every surface re-resolves together ──
 * It now LIVES in `app/persona.ts` and is re-exported here so that every
 * existing importer is untouched. It had to move for the data layer to reach
 * it: a governed resolve is made FOR a persona, and `data/queries.ts` importing
 * from `crm/` to learn which one is backwards (#1053 C). */
export { setPersona, usePersona, currentPersona } from '../app/persona'

/* ── experience reads ── */
export function usePersonas() {
  const { client } = useSolum()
  return useQuery({ queryKey: ['exp', 'personas'], queryFn: () => client.raw().select<Row>('core', 'persona') })
}
export function useViews(entity: string) {
  const { client } = useSolum()
  return useQuery({ queryKey: ['exp', 'views', entity], queryFn: () => client.raw().select<Row>('core', 'view', { where: { entity_type: entity } }) })
}
export function useForms(entity: string) {
  const { client } = useSolum()
  return useQuery({ queryKey: ['exp', 'forms', entity], queryFn: () => client.raw().select<Row>('core', 'form', { where: { entity_type: entity } }) })
}
export function useViewPrefs(entity: string) {
  const { client } = useSolum()
  return useQuery({ queryKey: ['exp', 'prefs', entity], queryFn: () => client.raw().select<Row>('core', 'user_view_pref', { where: { entity_type: entity, user_id: me() } }) })
}

/**
 * The one governed answer for a registry artifact (#2032).
 *
 * Raw registry reads above remain the switchers' INVENTORY. They never choose
 * the active row: only `core.resolve_experience` owns the user → tenant →
 * solution → assigned-persona → system cascade. Normal host calls deliberately
 * omit a persona; an explicit persona is an administrator preview.
 */
export function useRegistryExperience(entity: string, artifact: 'view' | 'form' | 'card' | 'lookup') {
  const { client } = useSolum()
  const activePersona = usePersona()
  const fixturePersona = isLive ? null : activePersona
  return useQuery({
    queryKey: ['experience-resolution', entity, artifact, 'console', fixturePersona],
    queryFn: async () => {
      const resolved = parseResolvedExperience(
        isLive
          ? await client.resolveExperience(entity, artifact, { host: 'console' })
          : await client.resolveExperience(entity, artifact, {
            host: 'console', previewPersona: activePersona,
          }),
      )
      if (resolved.state === 'refused') {
        throw new Error(resolved.issues.join('; ') || `The ${artifact} experience was refused`)
      }
      if (resolved.state === 'resolved') {
        const document = resolved.effective?.document
        if (!document || typeof document !== 'object' || Array.isArray(document)) {
          throw new Error(`The resolved ${artifact} experience has no usable document`)
        }
      }
      return resolved
    },
  })
}

/** Invalidate both inventory and the canonical answer after a registry write. */
function invalidateRegistryExperience(
  qc: ReturnType<typeof useQueryClient>, entity: string, artifact: 'view' | 'form' | 'card' | 'lookup',
) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['exp', `${artifact}s`, entity] }),
    qc.invalidateQueries({ queryKey: ['experience-resolution', entity, artifact] }),
    qc.invalidateQueries({ queryKey: ['experience-bundle', entity] }),
  ])
}

/* ── the 0026 filter grammar + 0027 firing: now the shared @solum/rules twin,
 * with $me bound to the caller here ($record & friends merge in via `binds`).
 * One JS implementation, tested against the grammar — no inline re-derivation.
 * Enforcement stays server-side (core.fire_rules over RLS); this is UX truth. ── */
export type { Leaf }
export const evalLeaf = (row: Row, f: Leaf, binds?: Record<string, unknown>): boolean =>
  evalLeafBase(row, f, { $me: me(), ...(binds ?? {}) })
export function useRules(trigger: string) {
  const { client } = useSolum()
  return useQuery({ queryKey: ['exp', 'rules', trigger], queryFn: () => client.raw().select<Row>('core', 'rule', { where: { trigger } }) })
}
export const fireRules = (rules: Row[], ctx: Row, binds?: Record<string, unknown>): RuleAction[] =>
  fireRulesBase(rules as Parameters<typeof fireRulesBase>[0], ctx, { $me: me(), ...(binds ?? {}) })
export const applyView = (rows: Row[], view: { filters?: Leaf[]; sort_spec?: unknown[] }): Row[] => {
  let out = rows.filter((r) => (view.filters ?? []).every((f) => evalLeaf(r, f)))
  const terms = parseSortSpec(view.sort_spec)
  if (terms.length > 0) {
    out = out.map((row, index) => ({ row, index })).sort((a, b) => {
      for (const { key, direction } of terms) {
        const av = a.row[key], bv = b.row[key]
        const compared = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''))
        if (compared !== 0) return compared * (direction === 'desc' ? -1 : 1)
      }
      // Explicit tie-break: the original governed row order stays stable even
      // on engines where Array#sort stability is not something we should ask a
      // customer-facing list to infer.
      return a.index - b.index
    }).map(({ row }) => row)
  }
  return out
}

/* ── save helpers (user forks + pins; RLS enforces fork-never-mutate live) ── */
export function useForkView(entity: string) {
  const { client } = useSolum(); const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: Row) => client.raw().insert<Row>('core', 'view', {
      entity_type: entity, scope: 'user', owner_id: me(), is_default: false, sort_order: 50,
      name: v.name, columns: v.columns, filters: v.filters, sort_spec: v.sort_spec,
      // The RENDER AXIS travels with the fork (core 2026081005). Forking a board
      // and getting a grid is the same defect `group_by` had in the config
      // bundle for 155 migrations — emitted on one side, dropped on the other.
      kind: v.kind ?? 'grid', group_by: v.group_by ?? null, date_col: v.date_col ?? null,
    }),
    onSuccess: () => invalidateRegistryExperience(qc, entity, 'view'),
  })
}
/**
 * Set or clear the caller's view pin for one entity — the one shipped USER
 * override of the experience cascade.
 *
 * `core.user_view_pref`'s primary key is `(user_id, entity_type)` and the table
 * has NO id column (`0030_personas_experience.sql:117-122`), so the row is
 * addressed by that key and delete-then-insert IS the upsert the key already
 * guarantees is a swap rather than a second row.
 *
 * It used to read the existing rows first and then
 * `delete('core','user_view_pref', String(p.id))` — `id=eq.undefined` on the
 * wire, which PostgREST answered 42703 to. So un-pinning, and every CHANGE of
 * pin, has thrown on every live deployment since the pin shipped; only the very
 * first pin for an entity ever worked, because that is the one path where the
 * loop over existing rows does not run. Invisible in fixture mode because the
 * fixture matched deletes on `r.id === id` — which is why the fixture now
 * carries the composite key too, and refuses an id-keyed delete on this table.
 *
 * `user_id` goes on the wire explicitly even though `uvp_own` (`for all using
 * (user_id = (select auth.uid()))`, same migration) already confines the
 * statement to the caller's own rows: it is half the key, so it has to be there
 * regardless, and RLS ANDs its own predicate — an explicit `user_id` can only
 * NARROW, never reach another user's pin. If `me()` ever disagreed with the
 * bearer, this matches nothing instead of the wrong row.
 */
export async function writeViewPin(
  client: Pick<SolumClient, 'raw'>, entity: string, viewId: string | null,
): Promise<void> {
  await client.raw().delete('core', 'user_view_pref', { user_id: me(), entity_type: entity })
  if (viewId) await client.raw().insert('core', 'user_view_pref', { user_id: me(), entity_type: entity, view_id: viewId })
}

export function usePinView(entity: string) {
  const { client } = useSolum(); const qc = useQueryClient()
  return useMutation({
    mutationFn: (viewId: string | null) => writeViewPin(client, entity, viewId),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['exp', 'prefs', entity] }),
      qc.invalidateQueries({ queryKey: ['experience-resolution', entity, 'view'] }),
      qc.invalidateQueries({ queryKey: ['experience-bundle', entity] }),
    ]),
  })
}

/* ── formatting + typed validation (kinds land in field_meta with 0031) ── */
export const fmtMoney = (v: unknown) => formatMoney(v, { gap: ' ', locale: 'en-US' })
export const fmtDate = (iso: unknown) => formatDate(iso)

export const normPhone = (v: string) => {
  let d = v.replace(/[^\d+]/g, '')
  if (d.startsWith('00')) d = '+' + d.slice(2)
  if (d.startsWith('0')) d = '+32' + d.slice(1)
  if (!d.startsWith('+')) d = '+32' + d
  const rest = d.replace('+32', '')
  return '+32 ' + (rest.length === 9 ? rest.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4') : rest)
}
export const POSTAL_RULES: Record<string, [RegExp, string]> = {
  BE: [/^\d{4}$/, 'BE postal codes are 4 digits'],
  NL: [/^\d{4}\s?[A-Za-z]{2}$/, 'NL postal codes look like 1234 AB'],
  FR: [/^\d{5}$/, 'FR postal codes are 5 digits'],
  DE: [/^\d{5}$/, 'DE postal codes are 5 digits'],
  LU: [/^\d{4}$/, 'LU postal codes are 4 digits'],
}
export const validateField = (kind: string, value: string, ctx?: { country?: string }): string | null => {
  if (value.trim() === '') return null
  if (kind === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()) ? null : 'That doesn’t look like an email address'
  if (kind === 'phone') { const n = value.replace(/\D/g, '').length; return n >= 9 && n <= 13 ? null : 'Enter a valid phone number (e.g. 0470 12 34 56)' }
  if (kind === 'postal' && ctx?.country && POSTAL_RULES[ctx.country]) {
    const [re, msg] = POSTAL_RULES[ctx.country]!
    return re.test(value.trim()) ? null : msg
  }
  return null
}

/* ── recents + pins — BOTH now live on the governed personal store.
 *
 * This block held `recordRecent` / `getRecents` / `getAllRecents` over
 * `localStorage['solum.recents']`, and a dead second pin store
 * (`getPins`/`isPinned`/`togglePin` over `localStorage['solum.pins']`) that no
 * caller had referenced since the drawer's star was pointed at `usePins`.
 *
 * #2325 ruling 3 moved recents to `core.user_setting`, the same governed
 * per-user store the pins ride — see `app/recents.ts` for the argument and for
 * the two-users defect that paid for it. The dead pin store went with them
 * rather than being left as a third answer to "what has this person kept".
 *
 * Nothing re-exports them from here. An import that still points at this file
 * is a caller that did not move, and the compiler says so. ── */
